import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaskService } from "@/services/taskService";
import { useTenant } from "./useTenant";
import type {
  Task,
  CreateTaskForm,
  TemplateStep,
  LookupValue,
  TaskAssigneeType,
  TemplateTask,
} from "@/types/task";

// Query keys for tasks
export const taskKeys = {
  all: (tid: string) => ["tasks", tid] as const,
  userTasks: (tid: string) => ["tasks", "user", tid] as const,
  byId: (tid: string, id: string) => ["tasks", tid, id] as const,
  templates: (tid: string) => ["templates", tid] as const,
  templateById: (tid: string, id: string) => ["templates", tid, id] as const,
  lookupValues: (tid: string, type: string) =>
    ["lookupValues", tid, type] as const,
};

// Get user tasks
export function useUserTasks() {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: taskKeys.userTasks(tenantId || ""),
    queryFn: () => TaskService.getUserTasks(tenantId!),
    enabled: !!tenantId,
  });
}

// Get single task
export function useTask(taskId: string) {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: taskKeys.byId(tenantId || "", taskId),
    queryFn: () => TaskService.getTask(taskId, tenantId!),
    enabled: !!taskId && !!tenantId,
  });
}

// Get templates
export function useTemplates() {
  const { tenantId } = useTenant();
  return useQuery<TemplateTask[]>({
    queryKey: taskKeys.templates(tenantId || ""),
    queryFn: () => TaskService.getTemplates(tenantId!),
    enabled: !!tenantId,
  });
}

// Get templates by layer
export function useTemplatesByLayer(layerName: string) {
  const { tenantId } = useTenant();
  return useQuery<TemplateTask[]>({
    queryKey: [
      ...taskKeys.templates(tenantId || ""),
      "layer",
      layerName,
    ] as const,
    queryFn: () => TaskService.getTemplatesByLayer(layerName, tenantId!),
    enabled: !!layerName && !!tenantId,
  });
}

// Get lookup values
export function useLookupValues(
  type: "category" | "task_status" | "step_status" | "priority"
) {
  const { tenantId } = useTenant();
  return useQuery<LookupValue[]>({
    queryKey: taskKeys.lookupValues(tenantId || "", type),
    queryFn: () => TaskService.getLookupValues(type, tenantId!),
    enabled: !!tenantId,
  });
}

// Create task
export function useCreateTask() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation({
    mutationFn: (taskData: CreateTaskForm) =>
      TaskService.createTask(taskData, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.userTasks(tenantId || ""),
      });
    },
  });
}

// Update task
export function useUpdateTask() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation({
    mutationFn: ({
      taskId,
      taskData,
    }: {
      taskId: string;
      taskData: Partial<Task>;
    }) => TaskService.updateTask(taskId, taskData, tenantId!),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.userTasks(tenantId || ""),
      });
      if (updatedTask) {
        queryClient.setQueryData(
          taskKeys.byId(tenantId || "", updatedTask.id),
          updatedTask
        );
        queryClient.invalidateQueries({
          queryKey: taskKeys.byId(tenantId || "", updatedTask.id),
        });
      }
    },
  });
}

// Delete task
export function useDeleteTask() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation({
    mutationFn: (taskId: string) => TaskService.deleteTask(taskId, tenantId!),
    onSuccess: (_data, taskId) => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.userTasks(tenantId || ""),
      });
      queryClient.removeQueries({
        queryKey: taskKeys.byId(tenantId || "", taskId),
      });
    },
  });
}

// Create task from template
export function useCreateTaskFromTemplate() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation({
    mutationFn: ({
      templateId,
      overrides,
    }: {
      templateId: string;
      overrides: CreateTaskForm;
    }) => TaskService.createTaskFromTemplate(templateId, overrides, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.userTasks(tenantId || ""),
      });
    },
  });
}

// Create template
export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation<
    TemplateTask,
    Error,
    {
      templateData: {
        name: string;
        description?: string;
        layer_name: string;
        category_id?: string;
        priority_id?: string;
        status_id?: string;
        default_assignee_type?: TaskAssigneeType;
        default_assignee?: string;
        default_due_days?: number;
        default_start_days?: number;
        auto_assign_steps?: boolean;
        require_photos?: boolean;
        require_geometry?: boolean;
        allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
      };
      templateSteps: TemplateStep[];
    }
  >({
    mutationFn: ({ templateData, templateSteps }) =>
      TaskService.createTemplate(templateData, templateSteps, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.templates(tenantId || ""),
      });
    },
  });
}

// Update template
export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation<
    TemplateTask,
    Error,
    {
      templateId: string;
      templateData: {
        name: string;
        description?: string;
        layer_name: string;
        category_id?: string;
        priority_id?: string;
        status_id?: string;
        default_assignee_type?: TaskAssigneeType;
        default_assignee?: string;
        default_due_days?: number;
        default_start_days?: number;
        auto_assign_steps?: boolean;
        require_photos?: boolean;
        require_geometry?: boolean;
        allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
      };
      templateSteps: TemplateStep[];
    }
  >({
    mutationFn: ({ templateId, templateData, templateSteps }) =>
      TaskService.updateTemplate(
        templateId,
        templateData,
        templateSteps,
        tenantId!
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.templates(tenantId || ""),
      });
    },
  });
}

// Delete template
export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  return useMutation<void, Error, string>({
    mutationFn: (templateId: string) =>
      TaskService.deleteTemplate(templateId, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: taskKeys.templates(tenantId || ""),
      });
    },
  });
}
